import { Nav } from "@/components/site/Nav";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";

const OWNER = "[Til eier: fyll inn juridisk navn, org.nr og adresse]";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl text-olive-deep">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-olive/80">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-cream text-olive-deep">
      <Seo title="Personvern – Helserebellen" description="Slik behandler Helserebellen personopplysninger: formål, rettslig grunnlag, lagringstid, dine rettigheter og hvem vi deler med." path="/personvern" />
      <Nav />
      <main className="mx-auto max-w-3xl px-5 md:px-8 pt-28 pb-24">
        <p className="font-script text-4xl text-terra">Personvern</p>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">Personvernerklæring</h1>
        <p className="mt-4 text-[15px] text-olive/70">Behandlingsansvarlig: {OWNER}. Kontakt: <a href="mailto:kontakt@helserebellen.no" className="underline text-terra">kontakt@helserebellen.no</a>. Sist oppdatert: august 2026.</p>

        <Section title="1. Hvilke opplysninger vi behandler">
          <p>Vi samler inn så lite som mulig, og aldri sensitive helseopplysninger (diagnoser, medisiner, tilstander) for kjøp av digitale planer eller nyhetsbrev.</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Kontaktskjema/nyhetsbrev:</strong> navn (valgfritt), e-post, melding (valgfritt), samtykketidspunkt.</li>
            <li><strong>Kjøp:</strong> e-post og ordre-/betalingsreferanse fra Stripe. Kortopplysninger håndteres av Stripe – de passerer aldri gjennom våre servere.</li>
            <li><strong>Levering:</strong> e-post og en sikker, tidsbegrenset nedlastingslenke.</li>
            <li><strong>Konto (hvis opprettet):</strong> navn, e-post, kryptert passord.</li>
            <li><strong>Statistikk (valgfritt):</strong> settes kun med ditt samtykke (se pkt. 7).</li>
          </ul>
        </Section>

        <Section title="2. Formål og rettslig grunnlag">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Levere kjøpte planer og kundeservice</strong> – oppfyllelse av avtale (GDPR art. 6 (1) b).</li>
            <li><strong>Nyhetsbrev/markedsføring</strong> – ditt samtykke (art. 6 (1) a). Kan trekkes når som helst.</li>
            <li><strong>Regnskap/bokføring av kjøp</strong> – rettslig forpliktelse (art. 6 (1) c).</li>
            <li><strong>Sikkerhet og forebygging av misbruk</strong> – berettiget interesse (art. 6 (1) f).</li>
            <li><strong>Valgfri statistikk</strong> – ditt samtykke (art. 6 (1) a).</li>
          </ul>
        </Section>

        <Section title="3. Databehandlere og mottakere">
          <p>Vi selger aldri opplysningene dine. Vi bruker følgende leverandører (databehandlere):</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Emergent</strong> – drift/hosting av nettsted og database.</li>
            <li><strong>Stripe</strong> – betalingsbehandling (Stripe-hostet checkout).</li>
            <li><strong>Transaksjonell e-post (Resend, via Emergent)</strong> – ordrebekreftelse/levering.</li>
            <li><strong>PostHog (via Emergent)</strong> – valgfri statistikk, kun med samtykke.</li>
          </ul>
          <p className="text-[13px] text-olive/60">[Til eier: bekreft hver leverandørs databehandleravtale (DPA), underleverandørliste og behandlingsregion. Noen leverandører kan behandle data utenfor EU/EØS – se pkt. 4. Ikke oppgi at alt forblir i EU/EØS uten å ha verifisert det.]</p>
        </Section>

        <Section title="4. Overføring utenfor EØS">
          <p>Enkelte leverandører (f.eks. Stripe og statistikkverktøy) kan behandle opplysninger utenfor EØS. Der det skjer, skal overføringen være beskyttet av et lovlig overføringsgrunnlag (f.eks. EUs standardavtaler / SCC).</p>
          <p className="text-[13px] text-olive/60">[Til eier: verifiser konkret overføringsgrunnlag for hver leverandør og oppdater denne teksten. Ikke påstå «kun EU/EØS» uten dokumentasjon.]</p>
        </Section>

        <Section title="5. Lagringstid">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Nyhetsbrev:</strong> til du melder deg av, deretter slettes påmeldingen.</li>
            <li><strong>Kjøps-/regnskapsposter:</strong> beholdes så lenge bokføringsloven krever (typisk 5 år). Ved sletteforespørsel anonymiserer vi personopplysningene, men beholder selve regnskapsposten.</li>
            <li><strong>Nedlastingslenker:</strong> utløper automatisk (30 dager), kan reutstedes ved behov.</li>
            <li><strong>Konto:</strong> til du ber om sletting.</li>
          </ul>
        </Section>

        <Section title="6. Dine rettigheter">
          <p>Du har rett til innsyn, retting, sletting, begrensning, dataportabilitet og å trekke samtykke. Send forespørsel til <a href="mailto:kontakt@helserebellen.no" className="underline text-terra">kontakt@helserebellen.no</a>, så behandler vi den så raskt som mulig.</p>
          <p>Du kan klage til <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="underline text-terra">Datatilsynet</a> hvis du mener behandlingen er i strid med regelverket.</p>
        </Section>

        <Section title="7. Informasjonskapsler (cookies) og statistikk">
          <p>Vi bruker kun nødvendige informasjonskapsler for at siden skal fungere. Valgfri statistikk (PostHog) aktiveres kun hvis du klikker «Godta alle» i samtykkebanneret. Velger du «Kun nødvendige», settes ingen statistikk. Du kan når som helst endre valget ved å tømme nettleserens lagring for dette nettstedet.</p>
        </Section>

        <Section title="8. Automatiserte avgjørelser">
          <p>Vi tar ingen automatiserte avgjørelser eller profilering med rettslig virkning for deg.</p>
        </Section>

        <div className="mt-12 border-t border-olive/12 pt-6 text-[14px]">
          <Link to="/" className="underline text-terra hover:text-olive-deep">← Til forsiden</Link>
          <span className="mx-3 text-olive/30">·</span>
          <Link to="/vilkar" className="underline text-terra hover:text-olive-deep">Kjøpsvilkår</Link>
          <span className="mx-3 text-olive/30">·</span>
          <Link to="/avmelding" className="underline text-terra hover:text-olive-deep">Meld av nyhetsbrev</Link>
        </div>
      </main>
    </div>
  );
}
